import { useState } from 'react';
import { Button, Card, Space, Tag, Typography, Form, Input, Select, App, Alert } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { trpc } from '../../lib/api';
import { usePartnerAuthStore } from '../../stores/partnerAuthStore';
import { ResponsiveTable, type ResponsiveColumnType } from '../../components/common/responsive';
import { withDevModalFilePath } from '../../components/common';

const { Title } = Typography;
const { Modal } = withDevModalFilePath('client/src/pages/partner/PartnerUsersPage.tsx');

interface PartnerUserRow {
  id: string;
  email: string;
  name: string;
  role: string;
  isActive: boolean;
  createdAt: Date | string;
}

interface InviteForm {
  email: string;
  name?: string;
  role?: string;
}

export function PartnerUsersPage() {
  const { message } = App.useApp();
  const { partnerUser } = usePartnerAuthStore();
  const [inviteOpen, setInviteOpen] = useState(false);
  const [form] = Form.useForm<InviteForm>();
  const utils = trpc.useUtils();

  const { data, isLoading, error } = trpc.partnerAdmin.partnerListUsers.useQuery(
    { page: 1, limit: 100 },
    { retry: false }
  );

  const inviteMutation = trpc.partnerAdmin.partnerInviteUser.useMutation({
    onSuccess: () => {
      message.success('招待メールを送信しました');
      setInviteOpen(false);
      form.resetFields();
      utils.partnerAdmin.partnerListUsers.invalidate();
    },
    onError: (err) => {
      message.error(err.message || '招待に失敗しました');
    },
  });

  const updateMutation = trpc.partnerAdmin.partnerUpdateUser.useMutation({
    onSuccess: () => {
      utils.partnerAdmin.partnerListUsers.invalidate();
    },
    onError: (err) => {
      message.error(err.message || '更新に失敗しました');
    },
  });

  const rows = (data?.users ?? []) as PartnerUserRow[];

  const columns: ResponsiveColumnType<PartnerUserRow>[] = [
    {
      title: 'ユーザー',
      dataIndex: 'name',
      key: 'name',
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 600 }}>{record.name}</div>
          <div style={{ fontSize: 12, color: '#888' }}>{record.email}</div>
        </div>
      ),
      mobileRole: 'title',
      mobilePriority: 1,
    },
    {
      title: 'ロール',
      dataIndex: 'role',
      key: 'role',
      render: (role) => role ?? '-',
      mobileRole: 'tag',
      mobilePriority: 2,
    },
    {
      title: '状態',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive) => (
        <Tag color={isActive ? 'green' : 'default'}>
          {isActive ? '有効' : '招待中'}
        </Tag>
      ),
      mobileRole: 'tag',
      mobilePriority: 3,
    },
    {
      title: '操作',
      key: 'actions',
      render: (_, record) => (
        <Button
          type="link"
          onClick={() =>
            updateMutation.mutate({
              partnerUserId: record.id,
              isActive: !record.isActive,
            })
          }
        >
          {record.isActive ? '無効化' : '再有効化'}
        </Button>
      ),
      mobileRole: 'hidden',
    },
  ];

  const handleInvite = () => {
    form.validateFields().then((values) => {
      inviteMutation.mutate(values);
    });
  };

  if (error) {
    return (
      <Alert
        type="warning"
        message="ユーザー管理は代理店管理者のみ利用できます"
        description={partnerUser?.role ? `現在のロール: ${partnerUser.role}` : undefined}
      />
    );
  }

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Title level={3} style={{ margin: 0 }}>ユーザー管理</Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setInviteOpen(true)}>
          招待
        </Button>
      </div>

      <Card>
        <ResponsiveTable
          dataSource={rows}
          columns={columns}
          loading={isLoading}
          rowKey="id"
          pagination={false}
          mobileCardConfig={{
            titleField: 'name',
          }}
        />
      </Card>

      <Modal
        title="ユーザー招待"
        open={inviteOpen}
        onCancel={() => {
          setInviteOpen(false);
          form.resetFields();
        }}
        onOk={handleInvite}
        okText="招待"
        cancelText="キャンセル"
        confirmLoading={inviteMutation.isPending}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="email"
            label="メールアドレス"
            rules={[
              { required: true, message: 'メールアドレスを入力してください' },
              { type: 'email', message: '有効なメールアドレスを入力してください' },
            ]}
          >
            <Input placeholder="user@example.com" />
          </Form.Item>
          <Form.Item name="name" label="氏名（任意）">
            <Input placeholder="担当者名" />
          </Form.Item>
          <Form.Item name="role" label="ロール" initialValue="OrgMember">
            <Select
              options={[
                { label: '管理者', value: 'OrgAdmin' },
                { label: 'メンバー', value: 'OrgMember' },
              ]}
            />
          </Form.Item>
        </Form>
      </Modal>
    </Space>
  );
}
